import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {BLUE_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';

import {WarehouseTableData} from '../warehouseModelsDataLayout';
import {
  DWD_MAX_COLLAPSED_COLUMNS_SHOWN,
  DWD_TABLE_FOOTER_HEIGHT,
  DWD_TABLE_HEADER_HEIGHT,
  DWD_TABLE_HEIGHT_PER_ROW,
  DWD_TABLE_WIDTH,
} from './diagram-layout/diagramLayoutConstants';
import {getUnpopulatedRelatedTables} from './diagram-layout/diagramLayoutHelpers';
import {DiagramGridCell} from './diagram-layout/diagramLayoutTypes';
import {useDiagramLayoutContext} from './DiagramLayoutContext';
import WarehouseDiagramTableFooter from './WarehouseDiagramTableFooter';
import WarehouseDiagramTableHeader from './WarehouseDiagramTableHeader';
import WarehouseDiagramTableRow from './WarehouseDiagramTableRow';

export interface WarehouseDiagramTableProps {
  schemaName: string;
  tableData: WarehouseTableData;
  isFocus: boolean;
  columnsExpanded: boolean;
  cell: DiagramGridCell;
}

export default function WarehouseDiagramTable({
  schemaName,
  tableData,
  columnsExpanded,
  isFocus,
  cell,
}: WarehouseDiagramTableProps): JSX.Element {
  const {layout, getCellTopLeftPixelPoint} = useDiagramLayoutContext();

  const columnsToShow = columnsExpanded
    ? tableData.columns.length
    : Math.min(tableData.columns.length, DWD_MAX_COLLAPSED_COLUMNS_SHOWN);
  const showFooter = columnsExpanded || columnsToShow < tableData.columns.length;

  const topLeftPixel = getCellTopLeftPixelPoint(cell);

  const header = (
    <div
      className={classNames(
        css(styles.headerWrapper),
        css(isFocus ? styles.focusHeaderWrapper : styles.notFocusHeaderWrapper)
      )}
    >
      <WarehouseDiagramTableHeader
        isFocus={isFocus}
        schemaName={schemaName}
        tableData={tableData}
        numberUnpopulatedRelatedTables={getUnpopulatedRelatedTables(layout, tableData).size}
      />
    </div>
  );

  const rows = tableData.columns.slice(0, columnsToShow).map((columnData, i) => (
    <div key={i} className={classNames('u-vertical-center-items', css(styles.tableRowWrapper))}>
      <WarehouseDiagramTableRow columnData={columnData} />
    </div>
  ));
  const tableBody = (
    <div
      className={classNames(
        css(styles.tableBodyWrapper),
        css(isFocus ? styles.focusTableBodyWrapper : styles.notFocusTableBodyWrapper)
      )}
    >
      {rows}
    </div>
  );

  const footer = (
    <div className={css(styles.footerWrapper)}>
      <WarehouseDiagramTableFooter tableName={tableData.name} expanded={columnsExpanded} />
    </div>
  );

  return (
    <div
      onMouseDown={(e) => {
        // Prevents panning the diagram by clicking and dragging a table.
        e.stopPropagation();
      }}
      style={{
        left: topLeftPixel.x,
        top: topLeftPixel.y,
      }}
      className={classNames(css(styles.wrapper))}
    >
      {header}
      {tableBody}
      {showFooter && footer}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    width: DWD_TABLE_WIDTH,
    overflow: 'hidden',
    // The parent wrapping div sets pointerEvents: 'none' so that mouse events are triggered when hovering
    // over relationships. This overrides the 'none' value so that normal mouse events are still triggered
    // on individual tables.
    pointerEvents: 'all',
    cursor: 'default',
    boxShadow: '0 0 5px #ccc',
    borderRadius: 3,
  },
  headerWrapper: {
    height: DWD_TABLE_HEADER_HEIGHT,
    overflow: 'hidden',
  },
  tableBodyWrapper: {
    backgroundColor: 'white',
    borderBottom: `1px solid ${GRAY_LIGHTER}`,
  },
  tableRowWrapper: {
    height: DWD_TABLE_HEIGHT_PER_ROW,
    overflow: 'hidden',
  },
  footerWrapper: {
    height: DWD_TABLE_FOOTER_HEIGHT,
    overflow: 'hidden',
    backgroundColor: 'white',
  },
  focusTableBodyWrapper: {
    borderTop: '1px solid white',
  },
  notFocusTableBodyWrapper: {
    borderTop: `1px solid ${GRAY_LIGHTER}`,
  },
  focusHeaderWrapper: {
    backgroundColor: BLUE_LIGHT,
    color: 'white',
  },
  notFocusHeaderWrapper: {
    backgroundColor: 'white',
    color: 'initial',
  },
});
